import React from "react";
import type { AgendaDayKpis } from "../agendaTypes";
import { cn } from "../../../utils/cn";

type KpiCardsProps = {
  kpis: AgendaDayKpis;
  compact?: boolean;
  className?: string;
};

function KpiCardShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <article className="flex min-h-[96px] flex-col rounded-xl border border-[#E8ECF1] bg-[#F7FAFC] px-4 py-3.5">
      <h3 className="whitespace-nowrap text-sm font-semibold text-[#718096]">
        {title}
      </h3>
      {children}
    </article>
  );
}

const KpiCards: React.FC<KpiCardsProps> = ({ kpis, compact = false, className }) => {
  if (compact) {
    return (
      <p className={cn("mb-4 text-sm text-[#718096] md:text-right", className)}>
        <span className="font-semibold text-[#2D3748]">
          {kpis.visits_today.total}
        </span>{" "}
        visitas ·{" "}
        <span className="font-semibold text-[#2D3748]">
          {kpis.occupancy.percent}%
        </span>{" "}
        ocupado ·{" "}
        <span className="font-semibold text-[#2D3748]">
          {kpis.free_next_3h.count}
        </span>{" "}
        livres
      </p>
    );
  }

  const trend = kpis.visits_today.trend_vs_same_weekday;

  return (
    <section className="mb-5 hidden grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid xl:grid-cols-4">
      <KpiCardShell title="Visitas hoje">
        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0">
          <span className="text-[1.75rem] font-semibold leading-none text-[#2D3748]">
            {kpis.visits_today.total}
          </span>
          <span className="text-[13px] text-[#718096]">
            {kpis.visits_today.in_person} presenciais · {kpis.visits_today.video}{" "}
            vídeo
          </span>
        </div>
        {trend != null ? (
          <p
            className={cn(
              "mt-1 text-[13px] font-medium",
              trend >= 0 ? "text-[#38A169]" : "text-[#E53E3E]"
            )}
          >
            {trend >= 0 ? "+" : ""}
            {trend} vs. mesmo dia da semana passada
          </p>
        ) : null}
      </KpiCardShell>

      <KpiCardShell title="Ocupação da agenda">
        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
          <span className="text-[1.75rem] font-semibold leading-none text-[#2D3748]">
            {kpis.occupancy.percent}%
          </span>
          <span className="text-[13px] text-[#718096]">
            {kpis.occupancy.occupied} de {kpis.occupancy.total} horários abertos
          </span>
        </div>
        <div className="mt-2 h-1 max-w-[120px] overflow-hidden rounded-full bg-[#EDF2F7]">
          <div
            className="h-full rounded-full bg-[#c4552b]"
            style={{ width: `${Math.min(kpis.occupancy.percent, 100)}%` }}
          />
        </div>
      </KpiCardShell>

      <KpiCardShell title="Realizadas até agora">
        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
          <span className="text-[1.75rem] font-semibold leading-none text-[#2D3748]">
            {kpis.completed.completed}
          </span>
          <span className="text-[13px] text-[#718096]">
            de {kpis.completed.expected} · {kpis.completed.no_show} não apareceu
          </span>
        </div>
        {kpis.completed.pending_post_visit > 0 ? (
          <p className="mt-1 text-[13px] text-[#DD6B20]">
            {kpis.completed.pending_post_visit} sem status pós-visita
          </p>
        ) : null}
      </KpiCardShell>

      <KpiCardShell title="Livres nas próximas 3h">
        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
          <span className="text-[1.75rem] font-semibold leading-none text-[#2D3748]">
            {kpis.free_next_3h.count}
          </span>
          <span className="text-[13px] text-[#718096]">horários</span>
        </div>
        {kpis.free_next_3h.earliest_time ? (
          <p className="mt-1 text-[13px] text-[#718096]">
            Mais cedo: {kpis.free_next_3h.earliest_time}
            {kpis.free_next_3h.earliest_consultant_name
              ? ` · ${kpis.free_next_3h.earliest_consultant_name}`
              : ""}
          </p>
        ) : (
          <p className="mt-1 text-[13px] text-[#718096]">
            Nenhum horário livre no período
          </p>
        )}
      </KpiCardShell>
    </section>
  );
};

export default KpiCards;
